import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import './styles/calendar.css';
import Clienti from './components/Clienti';
import Produse from './components/Produse';
import CalendarProductie from './components/CalendarProductie';
import StatiiLucru from './components/StatiiLucru';
import Operatori from './components/Operatori';
import OperatorConcedii from './components/OperatorConcedii';
import Setari from './components/Setari';
import Preforme from './components/Preforme';
import Saci from './components/Saci';
import { API_BASE_URL, apiUrl } from './config/api';
import CTC from './components/CTC';
import Mecanici from './components/Mecanici';

// API_BASE_URL și helper apiUrl sunt definite central în config/api.js

function ProductionApp({ session, onLogout, onBackToHub }) {
  const [currentUser] = useState(session.user);
  const [currentUserRole] = useState(session.user.role);
  const [activeTab, setActiveTab] = useState('clienti');
  const [clienti, setClienti] = useState([]);
  const [produse, setProduse] = useState([]);
  const [planificare, setPlanificare] = useState([]);
  const [statiiLucru, setStatiiLucru] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [setari, setSetari] = useState({});
  const [viewingOperatorConcedii, setViewingOperatorConcedii] = useState(null);

  useEffect(() => {
    fetchData();
    if (session.user.role === 'Operatori') {
      setActiveTab('calendar');
    }
    // Initial data load for the authenticated portal session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.user.role]);

  const fetchData = async () => {
    try {
      const [clientiRes, produseRes, planificareRes, statiiRes, operatoriRes, setariRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/clienti`),
        axios.get(`${API_BASE_URL}/produse`),
        axios.get(`${API_BASE_URL}/planificare-productie`),
        axios.get(`${API_BASE_URL}/statii-lucru`),
        axios.get(`${API_BASE_URL}/operatori`),
        axios.get(`${API_BASE_URL}/setari`)
      ]);

      setClienti(clientiRes.data);
      setProduse(produseRes.data);
      setPlanificare(planificareRes.data);
      setStatiiLucru(statiiRes.data);
      setOperatori(operatoriRes.data);
      
      // Convert setari array to object
      const setariObj = {};
      setariRes.data.forEach(item => {
        setariObj[item.cheie] = item.valoare;
      });
      setSetari(setariObj);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Sigur doriți să vă deconectați?')) {
      try {
        await axios.post(`${API_BASE_URL}/logout`);
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        onLogout();
      }
    }
  };

  // Role-based page access control
  const canAccessPage = (page, userRole) => {
    if (!userRole) return false;
    
    // Normalize role to handle variations
    const normalizedRole = userRole.trim();
    
  // Added 'saci' so that the Saci page/tab becomes visible for shared roles
  const shared = ['clienti', 'preforme', 'saci', 'produse', 'calendar', 'statii', 'operatori', 'ctc', 'mecanici', 'setari'];
    const pageAccess = {
      'Admin': shared,
      'Administrator': shared,
      'Office': shared,
      'Manager': shared, // Legacy support
      'Operatori': ['calendar'],
      'Operator': ['calendar'] // Alternative spelling
    };
    
    const hasAccess = pageAccess[normalizedRole]?.includes(page) || false;
    console.log(`🔍 Access check: "${normalizedRole}" -> "${page}" = ${hasAccess}`);
    return hasAccess;
  };

  // Get available tabs for current user role
  const getAvailableTabs = (userRole) => {
    if (!userRole) return [];
    
    const allTabs = [
  { id: 'clienti', label: 'Clienți' },
	{ id: 'preforme', label: 'Preforme' },
	{ id: 'saci', label: 'Saci' },
  { id: 'produse', label: 'Produse' },
      { id: 'calendar', label: 'Calendar Producție' },
      { id: 'statii', label: 'Stații de lucru' },
      { id: 'operatori', label: 'Operatori' },
      { id: 'ctc', label: 'CTC' },
      { id: 'mecanici', label: 'Mecanici' },
      { id: 'setari', label: 'Setări' }
    ];
    
    return allTabs.filter(tab => canAccessPage(tab.id, userRole));
  };

  const MainContent = () => {
    // Ensure we have the current user role before checking access
    if (!currentUserRole) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Se încarcă...</p>
        </div>
      );
    }
    
    console.log('🔍 ACCESS DEBUG:');
    console.log('  Current tab:', activeTab);
    console.log('  Current user role:', `"${currentUserRole}"`);
    console.log('  Role type:', typeof currentUserRole);
    console.log('  Can access current tab:', canAccessPage(activeTab, currentUserRole));
    
    // Check if user has access to current tab
    if (!canAccessPage(activeTab, currentUserRole)) {
      // Redirect to first available page
      const availableTabs = getAvailableTabs(currentUserRole);
      console.log('  Available tabs:', availableTabs);
      if (availableTabs.length > 0) {
        const firstTab = availableTabs[0].id;
        console.log('  Should redirect to:', firstTab);
        // Use setTimeout to avoid infinite re-render
        setTimeout(() => setActiveTab(firstTab), 0);
        return (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Se redirecționează...</p>
          </div>
        );
      }
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>🚫 Acces Restricționat</h2>
          <p>Nu aveți permisiunea să accesați această pagină.</p>
          <p>Rol utilizator: {currentUserRole}</p>
          <p>Pagină curentă: {activeTab}</p>
          <button onClick={() => {
            console.log('🔧 FORCE CLEAR - Clearing localStorage');
            localStorage.clear();
            window.location.reload();
          }}>
            🔄 Resetează și Reîncearcă
          </button>
        </div>
      );
    }

    return (
      <Routes>
        <Route path="/clienti" element={
          <Clienti 
            clienti={clienti} 
            setClienti={setClienti} 
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        } />
        <Route path="/preforme" element={
          <Preforme
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        } />
        <Route path="/saci" element={
          <Saci
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        } />
        <Route path="/produse" element={
          <Produse 
            produse={produse} 
            setProduse={setProduse} 
            statiiLucru={statiiLucru}
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        } />
        <Route path="/calendar" element={
          <CalendarProductie 
            planificare={planificare}
            setPlanificare={setPlanificare}
          />
        } />
        <Route path="/statii" element={
          <StatiiLucru 
            statiiLucru={statiiLucru} 
            setStatiiLucru={setStatiiLucru} 
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        } />
        <Route path="/operatori" element={
          <Operatori 
            operatori={operatori} 
            setOperatori={setOperatori} 
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        } />
        <Route path="/operatori/:operatorId/concedii" element={
          <OperatorConcedii 
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        } />
        <Route path="/setari" element={
          <Setari 
            setari={setari} 
            setSetari={setSetari} 
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        } />
        <Route path="*" element={<Navigate to={`/${activeTab}`} replace />} />
      </Routes>
    );
  };

  const availableTabs = getAvailableTabs(currentUserRole);

  const renderContent = () => {
    // Ensure we have the current user role before checking access
    if (!currentUserRole) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Se încarcă...</p>
        </div>
      );
    }
    
    console.log('🔍 ACCESS DEBUG:');
    console.log('  Current tab:', activeTab);
    console.log('  Current user role:', `"${currentUserRole}"`);
    console.log('  Role type:', typeof currentUserRole);
    console.log('  Can access current tab:', canAccessPage(activeTab, currentUserRole));
    
    // Check if user has access to current tab
    if (!canAccessPage(activeTab, currentUserRole)) {
      // Redirect to first available page
      const availableTabsForUser = getAvailableTabs(currentUserRole);
      console.log('  Available tabs:', availableTabs);
      if (availableTabs.length > 0) {
        const firstTab = availableTabs[0].id;
        console.log('  Should redirect to:', firstTab);
        // Use setTimeout to avoid infinite re-render
        setTimeout(() => setActiveTab(firstTab), 0);
        return (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Se redirecționează...</p>
          </div>
        );
      }
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>🚫 Acces Restricționat</h2>
          <p>Nu aveți permisiunea să accesați această pagină.</p>
          <p>Rol utilizator: {currentUserRole}</p>
          <p>Pagină curentă: {activeTab}</p>
          <button onClick={() => {
            console.log('🔧 FORCE CLEAR - Clearing localStorage');
            localStorage.clear();
            window.location.reload();
          }}>
            🔄 Resetează și Reîncearcă
          </button>
        </div>
      );
    }

    // If we're viewing an operator's concedii
    if (viewingOperatorConcedii !== null) {
      // Ensure we have a valid numeric ID
      const numericId = Number(viewingOperatorConcedii);
      if (isNaN(numericId)) {
        console.error(`Invalid operator ID for concedii: ${viewingOperatorConcedii}`);
        setViewingOperatorConcedii(null);
        return null;
      }
      
      console.log(`Rendering OperatorConcedii component for operator ID: ${numericId}`);
      return (
        <OperatorConcedii
          currentUser={currentUser}
          currentUserRole={currentUserRole}
          operatorId={numericId}
          goBack={() => setViewingOperatorConcedii(null)}
        />
      );
    }

    switch (activeTab) {
      case 'clienti':
        return <Clienti 
          clienti={clienti} 
          setClienti={setClienti} 
          currentUser={currentUser}
          currentUserRole={currentUserRole}
        />;
      case 'preforme':
        return <Preforme
          currentUser={currentUser}
          currentUserRole={currentUserRole}
        />;
      case 'saci':
        return <Saci
          currentUser={currentUser}
          currentUserRole={currentUserRole}
        />;
      case 'produse':
        return <Produse 
          produse={produse} 
          setProduse={setProduse} 
          statiiLucru={statiiLucru}
          currentUser={currentUser}
          currentUserRole={currentUserRole}
        />;
      case 'calendar':
        return <CalendarProductie 
          planificare={planificare}
          setPlanificare={setPlanificare}
        />;
      case 'statii':
        return <StatiiLucru 
          statiiLucru={statiiLucru} 
          setStatiiLucru={setStatiiLucru} 
          currentUser={currentUser}
          currentUserRole={currentUserRole}
        />;
      case 'operatori':
        return <Operatori 
          operatori={operatori} 
          setOperatori={setOperatori} 
          currentUser={currentUser}
          currentUserRole={currentUserRole}
          onViewConcedii={(operatorId) => setViewingOperatorConcedii(operatorId)}
        />;
      case 'ctc':
        return <CTC currentUser={currentUser} currentUserRole={currentUserRole} />;
      case 'mecanici':
        return <Mecanici currentUser={currentUser} currentUserRole={currentUserRole} />;
      case 'setari':
        return <Setari 
          setari={setari} 
          setSetari={setSetari} 
          currentUser={currentUser}
          currentUserRole={currentUserRole}
        />;
      default:
        return <div>Pagină necunoscută</div>;
    }
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-top">
          <div className="company-info">
            <img src="/ykhome/logo-yk-cu-an.png" alt="Logo" className="company-logo company-logo-light" />
            <img src="/ykhome/logo-yk-dark.png" alt="Logo" className="company-logo company-logo-dark" />
            <div className="company-text">
              <h1 className="company-name">Younger Karom Production Company</h1>
              <h2 className="system-name">Sistem de Planificare a Producției</h2>
            </div>
          </div>
          <div className="user-info">
            <span>Conectat ca: <strong>{currentUser?.username}</strong></span>
          </div>
        </div>
        <nav className="nav-tabs">
          <button className="nav-tab" type="button" onClick={onBackToHub}>
            ← Hub
          </button>
          {availableTabs.map(tab => (
            <button
              key={tab.id}
              className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
          <button 
            className="nav-tab logout-btn"
            onClick={handleLogout}
          >
            🚪 Logout
          </button>
        </nav>
      </header>

      <main className="app-content">
        {renderContent()}
      </main>
    </div>
  );
}

export default ProductionApp;
